// Which months the usage screen may offer, and the floor under them.

// ⚠⚠ NO USAGE EXISTS BEFORE THE GO-LIVE RESET, AND NONE EVER WILL. router#533 step 4 ran
// `go_live_reset()` on 12 September 2026 at 00:54 UTC, and it deletes every usage row there is:
// the raw metrics partitions, all three `*_metrics_daily` rollups, `node_online_accrual`,
// `node_storage_daily`, `usage_grants`, `usage_reports`, `usage_drawdowns`. So a month before this
// epoch holds nothing for any organization on any axis, permanently — and a picker that offers one
// draws an empty period, which reads as "you used nothing" rather than "we do not have it". That is
// the difference this floor exists to keep.
//
// ⚠ It is a constant rather than a reading, because the reset is a single past event at a fixed
// instant, not a retention window that moves. Nothing in the database publishes the date to
// `authenticated`: `schema_migrations` is not readable there, and no RPC answers "the earliest
// period that holds usage".
export const USAGE_EPOCH = '2026-09'

// The first DAY that holds usage: the day of the reset above. The over-time chart draws no day of
// the month before that is below it.
//
// ⚠ The same reason as the epoch, one level down. `get_org_usage_series` returns a row of zero for
// 1 to 11 September 2026, and a grey day of "0 B" says "you used nothing" about a day whose
// records were deleted. ⚠ The 12th itself is short by its first 54 minutes.
export const USAGE_EPOCH_DAY = '2026-09-12'

// The last `count` periods including the current one, newest first (for the period switcher), and
// never a month below the epoch. 'YYYY-MM' sorts lexicographically, so the floor is a string
// compare.
//
// ⚠ The current period is offered whatever the epoch says. A clock before the epoch is not a real
// state, but an empty picker is a screen with no period selected at all, which is worse than one
// stale entry.
export const recentPeriods = (count: number, now = new Date()): string[] => {
  const periods = Array.from({ length: count }, (_, i) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1))
    return d.toISOString().slice(0, 7)
  })
  const offered = periods.filter(p => p >= USAGE_EPOCH)
  return offered.length > 0 ? offered : periods.slice(0, 1)
}

// The period before `period`, or null when that month is below the epoch and so holds no usage.
// The over-time chart draws its last days in grey before the current period (Benoit, 2026-10-02).
//
// ⚠ Null, never an empty month: a read of a month below the epoch returns zeroes, and grey days of
// zero under a caption that names the month say "you used nothing", which is not what we know.
export const previousPeriod = (period: string): string | null => {
  const match = /^(\d{4})-(\d{2})$/.exec(period)
  if (!match) return null
  const month = Number(match[2])
  if (month < 1 || month > 12) return null
  const before = new Date(Date.UTC(Number(match[1]), month - 2, 1)).toISOString().slice(0, 7)
  return before >= USAGE_EPOCH ? before : null
}
